import { Button, FormGroup, InlineValuePopover, Input, Label, MinusIcon, PlusIcon, Tooltip } from "@xola/ui-kit";
import clsx from "clsx";
import { SecondaryButton } from "common";
import React, { ReactElement, useEffect, useRef, useState } from "react";

interface QuantitySpinnerProps {
    min?: number;
    max?: number;
    value: number;
    onChange: (value: number) => void;
    className?: string;
    classNames?: { button?: string; label?: string };
    text?: string;
    total?: number;
    disabled?: boolean;
    useLongPress?: boolean;
    isLoading?: boolean;
    size?: keyof typeof sizes;
    allowEditManually?: boolean;
    popoverParentId?: string;
    error?: string;
    disableDecrease?: boolean;
    minErrorMessage?: string;
    showTooltip?: boolean;
    manualMinErrorMessage?: string | ReactElement;
}

// TODO: Move to ui-kit once the design and features are finalized.
export const QuantitySpinner = ({
    value,
    // Minimum possible value for this item
    min = 0,
    // The max value possible for all the quantity spinners
    max = Number.POSITIVE_INFINITY,
    // Total is the number of every countable item in the booking. For example sum of all demographics
    total,
    onChange,
    disableDecrease = false,
    className,
    classNames,
    useLongPress = false,
    text = "",
    isLoading = false,
    disabled,
    size = "medium",
    allowEditManually = false,
    popoverParentId,
    error,
    minErrorMessage,
    manualMinErrorMessage,
    ...rest
}: QuantitySpinnerProps) => {
    const isDecreaseDisabled = value <= min || disabled;
    const isIncreaseDisabled = value >= max || (total && total >= max) || disabled;
    const intervalRef = useRef<NodeJS.Timer | null>(null);
    const [counter, setCounter] = useState(value);

    // stuff to make counter editable by keyboard
    const [editableCounter, setEditableCounter] = useState<number | null>(value);
    const [isOpen, setIsOpen] = useState(false);
    const counterRef = useRef<any>();
    const [focus, setFocus] = useState(false);
    const [isManualMaxError, setIsManualMaxError] = useState(false);
    const [isManualMinError, setIsManualMinError] = useState(false);
    // end

    let timeoutId: NodeJS.Timeout;

    useEffect(() => {
        setCounter(value);
        setEditableCounter(value);
    }, [value]);

    // helper used to move cursor to the most right position when edit the counter by keyboard
    useEffect(() => {
        const { current: element } = counterRef;
        if (focus && element) {
            const selection = window.getSelection();
            const range = document.createRange();
            range.setStart(element, 1);
            selection?.removeAllRanges();
            selection?.addRange(range);
            setFocus(false);
        }
    }, [focus, counterRef]);

    const startCounter = (decrement = true) => {
        if (intervalRef.current) {
            return;
        }

        intervalRef.current = setInterval(() => {
            if (decrement) {
                setCounter((value) => (value === min ? value : value - 1));
                setEditableCounter((value) => (value === min ? value : value! - 1));
            } else {
                setCounter((value) => (value === max ? value : value + 1));
                setEditableCounter((value) => (value === max ? value : value! + 1));
            }
        }, 100);
    };

    const stopCounter = () => {
        if (intervalRef.current) {
            clearInterval(intervalRef.current);
            onChange(counter);
            intervalRef.current = null;
        }
    };

    const handleCounterBlur = () => {
        const newValue =
            isManualMaxError || isManualMinError || (disableDecrease && editableCounter == null)
                ? value
                : editableCounter;
        setEditableCounter(newValue);
        setCounter(newValue ?? min);
        onChange(newValue ?? min);
        setIsOpen(false);
        setIsManualMaxError(false);
        setIsManualMinError(false);
    };

    const handleCounterManuallyChange = ({ target }: any) => {
        const rawInput = target.value;

        if (rawInput.length === 0) {
            setEditableCounter(null);
            return;
        }

        const newValue = Number.parseInt(rawInput, 10);
        if (Number.isNaN(newValue)) {
            setEditableCounter(counter);
        } else {
            setEditableCounter(newValue);
        }

        if (newValue < min) {
            setIsManualMinError(true);
        } else {
            setIsManualMinError(false);
        }

        if (total && newValue + (total - value) > max) {
            setIsManualMaxError(true);
        } else if (!total && newValue > max) {
            setIsManualMaxError(true);
        } else {
            setIsManualMaxError(false);
        }
    };

    return (
        <fieldset
            className={clsx("flex items-center", className)}
            disabled={disabled ?? isLoading}
            data-testid="quantity-spinner"
            {...rest}
        >
            {!disableDecrease ? (
                <Tooltip
                    content={
                        minErrorMessage ?? (
                            <span>
                                The minimum quantity needed in order to book is{" "}
                                <var data-var="minValue">{min}</var>
                            </span>
                        )
                    }
                    disabled={!isDecreaseDisabled}
                >
                    <SecondaryButton
                        size="small"
                        className={clsx(
                            "text-gray-darker flex-shrink-0",
                            sizes[size].button,
                            classNames?.button,
                            error && "border-danger",
                        )}
                        data-testid="quantity-spinner-decrease-button"
                        disabled={isDecreaseDisabled ?? false}
                        onClick={() => {
                            const newValue = value > min ? value - 1 : value;
                            onChange(newValue);
                            setEditableCounter(newValue);

                            if (useLongPress) {
                                stopCounter();
                            }
                        }}
                        onMouseDown={() => {
                            if (useLongPress) {
                                timeoutId = setTimeout(() => {
                                    startCounter(true);
                                }, 400);
                            }
                        }}
                        onMouseUp={() => {
                            clearTimeout(timeoutId);
                            if (useLongPress) {
                                stopCounter();
                            }
                        }}
                        onMouseLeave={() => {
                            clearTimeout(timeoutId);
                            if (useLongPress) {
                                stopCounter();
                            }
                        }}
                    >
                        <MinusIcon className="top-0" />
                    </SecondaryButton>
                </Tooltip>
            ) : null}

            <div className={clsx("mx-3 min-w-[30px] text-center", classNames?.label)}>
                <InlineValuePopover
                    showArrow={false}
                    text={
                        <span
                            className={clsx(classNames?.label, error && "text-danger")}
                            data-testid="quantity-spinner-capacity"
                        >
                            {Number(Math.abs(counter))}
                        </span>
                    }
                    isOpen={allowEditManually && isOpen}
                    classNames={{ text: "text-white" }}
                    appendTo={popoverParentId && (() => document.querySelector(`#${popoverParentId}`))}
                    distance={15}
                    onClick={() => setIsOpen(!isOpen)}
                    onClickOutside={handleCounterBlur}
                >
                    <FormGroup className="!mb-0 flex flex-col">
                        <div className="flex space-x-2">
                            <div className="flex flex-col space-x-2">
                                <Input
                                    type="tel" // TO show only numbers and yet avoid the up and down number arrows that come with type number
                                    className={clsx(manualMinErrorMessage ? "w-auto" : "w-30")}
                                    value={editableCounter}
                                    isError={isManualMaxError || isManualMinError}
                                    onChange={handleCounterManuallyChange}
                                />
                                {isManualMaxError && (
                                    <Label className="!text-danger !mb-0 text-left">Quantity cannot exceed {max}</Label>
                                )}
                                {isManualMinError && (
                                    <Label className="!text-danger tracking-tightest !mb-0 w-auto break-normal text-left">
                                        {manualMinErrorMessage ?? "Decreasing guest count is not supported from here"}
                                    </Label>
                                )}
                            </div>
                            <Button disabled={isManualMaxError || isManualMinError} onClick={handleCounterBlur}>
                                Apply
                            </Button>
                        </div>
                    </FormGroup>
                </InlineValuePopover>
                <div className="leading-none">{text}</div>
            </div>

            {/* TODO: Refactor this button, it has a lot in common with the minus button above */}
            <Tooltip
                content={
                    <span>
                        You have exceeded the maximum capacity <var data-var="maxValue">{max}</var>.
                    </span>
                }
                disabled={!isIncreaseDisabled}
            >
                <SecondaryButton
                    size="small"
                    data-testid="quantity-spinner-increase-button"
                    className={clsx(
                        "text-gray-darker flex-shrink-0",
                        sizes[size].button,
                        classNames?.button,
                        error && "border-danger",
                    )}
                    disabled={isIncreaseDisabled ?? false}
                    onClick={() => {
                        const newValue = value < max ? value + 1 : value;
                        onChange(newValue);
                        setEditableCounter(newValue);

                        if (useLongPress) {
                            stopCounter();
                        }
                    }}
                    onMouseDown={() => {
                        if (useLongPress) {
                            timeoutId = setTimeout(() => {
                                startCounter(false);
                            }, 400);
                        }
                    }}
                    onMouseUp={() => {
                        clearTimeout(timeoutId);
                        if (useLongPress) {
                            stopCounter();
                        }
                    }}
                    onMouseLeave={() => {
                        clearTimeout(timeoutId);
                        if (useLongPress) {
                            stopCounter();
                        }
                    }}
                >
                    <PlusIcon className="top-0" />
                </SecondaryButton>
            </Tooltip>
        </fieldset>
    );
};

const sizes = {
    medium: {
        button: "h-6 w-6 px-0.5",
        value: "text-base",
    },

    large: {
        button: "h-10 w-10 p-1",
        value: "text-lg font-bold",
    },
};
